// Tiny WebAudio sound synthesizer for Hobbli Pinball.
// It deliberately avoids external sound files so the game is a true drop-in.

export function createPinballAudio() {
  let context = null;
  let enabled = true;

  const ensureContext = () => {
    if (!enabled) return null;
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return null;
    if (!context) context = new AudioContext();
    if (context.state === "suspended") context.resume().catch(() => {});
    return context;
  };

  const tone = (frequency, duration = 0.06, type = "square", gain = 0.035, endFrequency = null, delay = 0) => {
    const ctx = ensureContext();
    if (!ctx) return;
    const start = ctx.currentTime + delay;
    const oscillator = ctx.createOscillator();
    const volume = ctx.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, start);
    if (endFrequency) oscillator.frequency.exponentialRampToValueAtTime(Math.max(30, endFrequency), start + duration);
    volume.gain.setValueAtTime(0.0001, start);
    volume.gain.exponentialRampToValueAtTime(gain, start + 0.006);
    volume.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    oscillator.connect(volume);
    volume.connect(ctx.destination);
    oscillator.start(start);
    oscillator.stop(start + duration + 0.02);
  };

  const noise = (duration = 0.05, gain = 0.025) => {
    const ctx = ensureContext();
    if (!ctx) return;
    const length = Math.max(1, Math.floor(ctx.sampleRate * duration));
    const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < length; i += 1) data[i] = Math.random() * 2 - 1;
    const source = ctx.createBufferSource();
    const volume = ctx.createGain();
    source.buffer = buffer;
    volume.gain.setValueAtTime(gain, ctx.currentTime);
    volume.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + duration);
    source.connect(volume);
    volume.connect(ctx.destination);
    source.start();
  };

  const play = (name) => {
    if (!enabled) return;
    switch (name) {
      case "flipper":
        noise(0.025, 0.026);
        tone(125, 0.035, "square", 0.022, 90);
        break;
      case "bumper":
        tone(480, 0.07, "square", 0.033, 760);
        tone(950, 0.035, "sine", 0.018, null, 0.018);
        break;
      case "sling":
        tone(210, 0.045, "square", 0.025, 320);
        break;
      case "target":
        tone(690, 0.05, "square", 0.026, 520);
        break;
      case "lane":
        tone(920, 0.06, "sine", 0.024, 1180);
        break;
      case "reactor":
        tone(180, 0.14, "sawtooth", 0.03, 520);
        tone(720, 0.08, "sine", 0.02, 980, 0.05);
        break;
      case "launch":
        tone(135, 0.18, "sawtooth", 0.024, 480);
        break;
      case "mission":
        [440, 554, 659, 880].forEach((f, i) => tone(f, 0.09, "square", 0.025, null, i * 0.075));
        break;
      case "jackpot":
        [523, 659, 784, 1046].forEach((f, i) => tone(f, 0.13, "sawtooth", 0.03, f * 1.08, i * 0.07));
        break;
      case "extra":
        [660, 880, 990, 1320].forEach((f, i) => tone(f, 0.1, "sine", 0.026, null, i * 0.075));
        break;
      case "drain":
        tone(210, 0.28, "sawtooth", 0.025, 62);
        break;
      case "start":
        [330, 440, 660].forEach((f, i) => tone(f, 0.08, "square", 0.025, null, i * 0.06));
        break;
      default:
        tone(360, 0.04, "sine", 0.018);
    }
  };

  return {
    play,
    setEnabled(value) {
      enabled = !!value;
    },
    getEnabled() {
      return enabled;
    },
    dispose() {
      if (context && context.state !== "closed") context.close().catch(() => {});
      context = null;
    },
  };
}
